import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import type { InAppNotification } from '../../types/notification.types';
import { formatDistanceToNow } from 'date-fns';

interface NotificationItemProps {
  notification: InAppNotification;
  onMarkAsRead: (id: string) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
}

/**
 * Individual Notification Item Component
 * Displays a single notification with actions
 */
export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onArchive,
  onDelete,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isUnread = notification.status === 'UNREAD';

  const handleClick = () => {
    if (isUnread) {
      onMarkAsRead(notification._id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const getPriorityColor = () => {
    switch (notification.priority) {
      case 'URGENT':
        return theme === 'light' ? 'text-rose-600' : 'text-rose-400';
      case 'HIGH':
        return theme === 'light' ? 'text-orange-600' : 'text-orange-400';
      case 'NORMAL':
        return theme === 'light' ? 'text-amber-600' : 'text-amber-400';
      default:
        return theme === 'light' ? 'text-slate-600' : 'text-slate-400';
    }
  };

  const getPriorityIcon = () => {
    if (notification.priority === 'URGENT' || notification.priority === 'HIGH') {
      return (
        <svg className={`w-4 h-4 ${getPriorityColor()}`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
        </svg>
      );
    }
    return null;
  };

  return (
    <div
      className={`group relative border-b transition-all ${
        theme === 'light'
          ? 'border-slate-200 hover:bg-sky-50/50'
          : 'border-slate-700 hover:bg-slate-800/50'
      } ${isUnread ? (theme === 'light' ? 'bg-sky-50/30' : 'bg-cyan-900/10') : ''}`}
    >
      <div
        onClick={handleClick}
        className={`px-4 py-3 cursor-pointer ${notification.actionUrl ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-start gap-3">
          {/* Unread Indicator */}
          {isUnread && (
            <div
              className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                theme === 'light' ? 'bg-sky-500' : 'bg-cyan-400'
              }`}
            />
          )}

          {/* Priority Icon */}
          {getPriorityIcon() && (
            <div className="mt-1 shrink-0">{getPriorityIcon()}</div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4
                className={`text-sm leading-tight ${
                  isUnread
                    ? theme === 'light'
                      ? 'font-semibold text-slate-900'
                      : 'font-semibold text-white'
                    : theme === 'light'
                    ? 'font-medium text-slate-700'
                    : 'font-medium text-slate-300'
                }`}
              >
                {notification.title}
              </h4>
              <span
                className={`text-xs shrink-0 ${
                  theme === 'light' ? 'text-slate-500' : 'text-slate-400'
                }`}
              >
                {formatDistanceToNow(new Date(notification.createdOn), { addSuffix: true })}
              </span>
            </div>

            {notification.message && (
              <p
                className={`mt-1 text-sm line-clamp-2 ${
                  theme === 'light' ? 'text-slate-600' : 'text-slate-400'
                }`}
              >
                {notification.message}
              </p>
            )}

            {/* Action Button */}
            {notification.actionUrl && notification.actionLabel && (
              <button
                className={`mt-2 text-xs font-medium transition-colors ${
                  theme === 'light'
                    ? 'text-sky-600 hover:text-sky-700'
                    : 'text-cyan-400 hover:text-cyan-300'
                }`}
              >
                {notification.actionLabel} →
              </button>
            )}
          </div>

          {/* Action Buttons (visible on hover) */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {notification.status !== 'ARCHIVED' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive(notification._id);
                }}
                className={`p-1.5 rounded transition-colors ${
                  theme === 'light'
                    ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                    : 'text-slate-500 hover:text-slate-300 hover:bg-slate-700'
                }`}
                title="Archive"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                  />
                </svg>
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(notification._id);
              }}
              className={`p-1.5 rounded transition-colors ${
                theme === 'light'
                  ? 'text-slate-400 hover:text-rose-600 hover:bg-rose-50'
                  : 'text-slate-500 hover:text-rose-400 hover:bg-rose-900/20'
              }`}
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
